import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import type { PollType } from '@/api/polls'

/**
 * Локальный SPA-state для onboarding-wizard'а опросов (slides 18/19/20 из
 * design_v2_pool/onboarding.html). Живёт в памяти пока юзер в цепочке
 * `/onboarding/polls/*`. После success — reset.
 *
 * НЕ путать с CreatePollPage (`/polls/new`) — это отдельный full-form flow.
 * Wizard — упрощённый path под template picker + preview + publish в 1 клик.
 */
export type PollWizardTemplateId = 'pulse' | 'topic' | 'multi' | 'quiz'

interface TemplateDef {
  id: PollWizardTemplateId
  type: PollType
  question: string
  options: { text: string; isCorrect?: boolean }[]
}

/**
 * Шаблоны — 1-в-1 с design_v2_pool/onboarding.html slide 18.
 * `letter` — не задаём (backend авто-присвоит А/Б/В).
 */
export const POLL_WIZARD_TEMPLATES: Record<PollWizardTemplateId, TemplateDef> = {
  pulse: {
    id: 'pulse',
    type: 'single',
    question: 'Как вам последние посты в канале?',
    options: [
      { text: 'Огонь, продолжайте' },
      { text: 'Нормально' },
      { text: 'Хочу больше рецептов' },
      { text: 'Меньше рекламы' },
    ],
  },
  topic: {
    id: 'topic',
    type: 'single',
    question: 'О чём написать в следующем посте?',
    options: [{ text: '' }, { text: '' }, { text: '' }],
  },
  multi: {
    id: 'multi',
    type: 'multi',
    question: 'Что из этого вы уже пробовали?',
    options: [{ text: '' }, { text: '' }, { text: '' }, { text: '' }],
  },
  quiz: {
    id: 'quiz',
    type: 'quiz',
    question: 'Проверим, кто настоящий эксперт?',
    options: [{ text: '', isCorrect: true }, { text: '' }, { text: '' }, { text: '' }],
  },
}

export interface PollsWizardState {
  templateId: PollWizardTemplateId | null
  type: PollType
  question: string
  /** Опциональное описание под вопросом (до 500 codepoints, аналог CreatePollPage). */
  description: string
  options: { text: string; isCorrect?: boolean }[]
  anonymous: boolean
  closeIn24h: boolean
  /** channelUUID из magic-payload или ?channelUUID= query — куда опубликуем. */
  channelUUID: string | null
  /** pollId виральной подписи — source of truth события первого захода. */
  ref: string | null
}

interface PollsWizardContextValue {
  state: PollsWizardState
  setTemplate: (id: PollWizardTemplateId) => void
  setType: (t: PollType) => void
  setQuestion: (q: string) => void
  setDescription: (d: string) => void
  setOptionText: (index: number, text: string) => void
  addOption: () => void
  removeOption: (index: number) => void
  setCorrectIndex: (index: number) => void
  setAnonymous: (v: boolean) => void
  setCloseIn24h: (v: boolean) => void
  setChannelUUID: (uuid: string | null) => void
  setRef: (ref: string | null) => void
  reset: () => void
}

const MAX_OPTIONS = 8
const MIN_OPTIONS = 2

const INITIAL_STATE: PollsWizardState = {
  templateId: null,
  type: 'single',
  question: '',
  description: '',
  options: [],
  // Виктор 2026-07-28: чекбоксы «Анонимный» и «Закрыть через 24ч» по дефолту
  // выключены (юзер сам решает включать). Publish → closeMode='manual'.
  anonymous: false,
  closeIn24h: false,
  channelUUID: null,
  ref: null,
}

const PollsWizardContext = createContext<PollsWizardContextValue | null>(null)

export function PollsWizardProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PollsWizardState>(INITIAL_STATE)

  const value = useMemo<PollsWizardContextValue>(
    () => ({
      state,
      setTemplate: (id) => {
        const tpl = POLL_WIZARD_TEMPLATES[id]
        setState((s) => ({
          ...s,
          templateId: id,
          type: tpl.type,
          question: tpl.question,
          options: tpl.options.map((o) => ({ ...o })),
        }))
      },
      setType: (t) => {
        setState((s) => {
          // quiz требует ровно 1 isCorrect=true. Если переключились на quiz с single/multi —
          // ставим первый вариант правильным. При обратном переключении — сбрасываем.
          const options = s.options.map((o, i) =>
            t === 'quiz' ? { ...o, isCorrect: i === 0 } : { text: o.text },
          )
          return { ...s, type: t, options }
        })
      },
      setQuestion: (q) => setState((s) => ({ ...s, question: q })),
      setDescription: (d) => setState((s) => ({ ...s, description: d })),
      setOptionText: (index, text) => {
        setState((s) => ({
          ...s,
          options: s.options.map((o, i) => (i === index ? { ...o, text } : o)),
        }))
      },
      addOption: () => {
        setState((s) => {
          if (s.options.length >= MAX_OPTIONS) return s
          return { ...s, options: [...s.options, { text: '' }] }
        })
      },
      removeOption: (index) => {
        setState((s) => {
          if (s.options.length <= MIN_OPTIONS) return s
          const filtered = s.options.filter((_, i) => i !== index)
          // quiz: если удалили правильный ответ — первый становится правильным.
          if (s.type === 'quiz' && !filtered.some((o) => o.isCorrect)) {
            return { ...s, options: filtered.map((o, i) => ({ ...o, isCorrect: i === 0 })) }
          }
          return { ...s, options: filtered }
        })
      },
      setCorrectIndex: (index) => {
        setState((s) => ({
          ...s,
          options: s.options.map((o, i) => ({ ...o, isCorrect: i === index })),
        }))
      },
      setAnonymous: (v) => setState((s) => ({ ...s, anonymous: v })),
      setCloseIn24h: (v) => setState((s) => ({ ...s, closeIn24h: v })),
      setChannelUUID: (uuid) => setState((s) => ({ ...s, channelUUID: uuid })),
      setRef: (ref) => setState((s) => ({ ...s, ref })),
      reset: () => setState(INITIAL_STATE),
    }),
    [state],
  )

  return <PollsWizardContext.Provider value={value}>{children}</PollsWizardContext.Provider>
}

export function usePollsWizard(): PollsWizardContextValue {
  const ctx = useContext(PollsWizardContext)
  if (!ctx) throw new Error('usePollsWizard must be used inside <PollsWizardProvider>')
  return ctx
}
